import { HOME_BLOCK_ARTICLE_COUNTS } from "@config/home";
import { NDR_FEATURE_ENABLE } from "@helper/getEnvVariables";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import BHBasePage, { type BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { USFWebSiteSchema } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import fetchHomeData, { PayloadResponseType } from "./Home.server";
import HomeMetaData from "./HomeMetaData";
import HomePageNew from "./HomePageLayout/HomePageNew";

const adsSectionName = "home";

export function meta() {
  return [getMetaData(HomeMetaData(adsSectionName).metaTags)];
}

export default function HomePage() {
  const dataLoaderResponse: TRouteWithoutRedirect<PayloadResponseType, string> =
    useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const {
    trendingTopics,
    KakiMakanBanner,
    storyQueue,
    latestArticles,
    ndrArticles,
    latestSgArticles,
    latestMaArticles,
    kakiJalanArticles,
    kakiMakanArticles,
    dnaDuniaArticles,
    latestGayaArticles,
    registeredArticles,
    latestDuniaArticles,
    myBHArticles,
  } = dataLoaderResponse.payload;

  const pageAdTargetValue = PageAdTargetingTypeEnum.HOME;

  const basePageProps: BHBasePageProps = {
    uniqueSectionName: adsSectionName,
    pageAdTargetValue: pageAdTargetValue,
    hasLb1Ads: true,
    ...(NDR_FEATURE_ENABLE && {
      bodyWrapperStyle: "my-0 lg:mt-0 mb-3 lg:mb-md",
    }),
  };

  return (
    <BHBasePage {...basePageProps}>
      <USFWebSiteSchema
        name="Berita Harian"
        url="https://www.beritaharian.sg/"
        searchUrl="https://www.beritaharian.sg/search/site/{search_term_string}"
      />
      <HomePageNew
        topStoryQueue={storyQueue.slice(0, HOME_BLOCK_ARTICLE_COUNTS.DEFAULT)}
        latestFreeArticle={latestArticles}
        ndrArticle={ndrArticles}
        latestSgArticle={latestSgArticles}
        latestMaArticle={latestMaArticles}
        kakiJalanArticle={kakiJalanArticles}
        kakiMakanArticle={kakiMakanArticles}
        dnaDuniaArticle={dnaDuniaArticles}
        latestGayaArticle={latestGayaArticles}
        registeredArticle={registeredArticles}
        latestDuniaArticle={latestDuniaArticles}
        adsSectionName={adsSectionName}
        pageAdTargetValue={pageAdTargetValue}
        KakiMakanBanner={KakiMakanBanner}
        myBHServerArticles={myBHArticles}
        trendingTopics={trendingTopics}
      />
    </BHBasePage>
  );
}

export async function loader() {
  const data = await fetchHomeData();
  const { gaData } = HomeMetaData(adsSectionName);

  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<PayloadResponseType, string>
  >;
}
